import { StatusPill } from "@/components/iris";
import type { StatusPillVariant } from "@/components/iris";
import { api } from "@/lib/api";
import type { ReconciliationSession } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";

const SEV_VARIANT: Record<ReconciliationSession["prod_severity"], StatusPillVariant> = {
  clean: "ok",
  pp_warn: "warn",
  skipped: "idle",
  failed: "err",
  unknown: "idle",
};

const SEV_LABEL: Record<ReconciliationSession["prod_severity"], string> = {
  clean: "Clean",
  pp_warn: "PP Warn",
  skipped: "Skipped",
  failed: "Failed",
  unknown: "\u2014",
};

function SeverityBadge({ sev }: { sev: ReconciliationSession["prod_severity"] }) {
  return <StatusPill variant={SEV_VARIANT[sev]}>{SEV_LABEL[sev]}</StatusPill>;
}

function useReconciliationSessions() {
  return useQuery({
    queryKey: ["pipeline", "sessions"],
    queryFn: () => api<ReconciliationSession[]>("/api/ops/v1/pipeline/sessions"),
    refetchInterval: 60_000,
  });
}

function formatDate(iso: string | null): string {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

// Per-batch reconciliation now has its own bucket per env (real_time_ingestion_dev
// vs real_time_ingestion_prod), so a prod session has nothing to say about dev
// ingestion and vice versa. Drop cross-env columns based on VITE_IRIS_ENV.
const ENV: "prod" | "dev" | "both" =
  (import.meta.env.VITE_IRIS_ENV as "prod" | "dev" | undefined) || "both";
const SHOW_PROD = ENV === "prod" || ENV === "both";
const SHOW_DEV = ENV === "dev" || ENV === "both";
const COL_COUNT = 2 + (SHOW_PROD ? 3 : 0) + (SHOW_DEV ? 3 : 0); // session+scraped + 3 per env shown

export function SessionTable() {
  const { data: sessions, isLoading } = useReconciliationSessions();

  return (
    <div className="iris-card" style={{ overflow: "hidden" }}>
      {/* Header */}
      <div
        style={{
          borderBottom: "1px solid var(--iris-line-1)",
          padding: "12px 18px",
        }}
      >
        <h3 className="iris-h3" style={{ fontSize: 14, margin: 0 }}>
          Session Reconciliation
        </h3>
        <p className="iris-meta" style={{ marginTop: 2 }}>
          Per-batch scrape vs ingestion status
        </p>
      </div>

      {/* Table */}
      <table className="iris-table">
        <thead>
          <tr>
            <th>Session / Avatar</th>
            <th>Scraped</th>
            {SHOW_PROD && (
              <>
                <th>P-Ing</th>
                <th>P-DB</th>
                <th>Prod Sev</th>
              </>
            )}
            {SHOW_DEV && (
              <>
                <th>D-Ing</th>
                <th>D-DB</th>
                <th>Dev Sev</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td
                colSpan={COL_COUNT}
                className="iris-td--muted"
                style={{ textAlign: "center", padding: "16px 12px" }}
              >
                Loading...
              </td>
            </tr>
          ) : (sessions ?? []).length === 0 ? (
            <tr>
              <td
                colSpan={COL_COUNT}
                style={{ textAlign: "center", color: "var(--iris-fg-4)", padding: "16px 12px" }}
              >
                No sessions in the last window
              </td>
            </tr>
          ) : (
            (sessions ?? []).map((s) => (
              <tr key={s.session_key}>
                {/* Session key + avatar */}
                <td>
                  <div
                    style={{
                      fontWeight: 500,
                      color: "var(--iris-fg-1)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s.avatar}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--iris-font-mono)",
                      fontSize: 10,
                      color: "var(--iris-fg-5)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s.session_key}
                  </div>
                  {s.timestamp && (
                    <div style={{ fontSize: 10, color: "var(--iris-fg-5)" }}>
                      {formatDate(s.timestamp)}
                    </div>
                  )}
                </td>

                {/* Scraped */}
                <td className="iris-td--num">
                  {s.scraped_groups ?? 0}g / {(s.scraped_members ?? 0).toLocaleString()}m
                </td>

                {SHOW_PROD && (
                  <>
                    <td className="iris-td--num" style={{ color: "var(--iris-env-prod)" }}>
                      {s.prod_ingested?.toLocaleString() ?? "\u2014"}
                    </td>
                    <td className="iris-td--num" style={{ color: "var(--iris-env-prod)" }}>
                      {s.prod_db?.toLocaleString() ?? "\u2014"}
                    </td>
                    <td>
                      <SeverityBadge sev={s.prod_severity} />
                    </td>
                  </>
                )}

                {SHOW_DEV && (
                  <>
                    <td className="iris-td--num" style={{ color: "var(--iris-env-dev)" }}>
                      {s.dev_ingested?.toLocaleString() ?? "\u2014"}
                    </td>
                    <td className="iris-td--num" style={{ color: "var(--iris-env-dev)" }}>
                      {s.dev_db?.toLocaleString() ?? "\u2014"}
                    </td>
                    <td>
                      <SeverityBadge sev={s.dev_severity} />
                    </td>
                  </>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
